import React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import type { MonthPeekSelection } from '../../calendar/types';
import { MONTH_TIER, monthPerfTier } from '../../calendar/monthUtils';
import { calendarEyebrow } from '../../calendar/utils';

export interface MonthPeekModalProps {
  peek: MonthPeekSelection | null;
  onClose: () => void;
  onOpenDay?: () => void;
}

function monthPeekTitle(cell: MonthPeekSelection['cell']): string {
  if (cell.date) {
    const d = new Date(`${cell.date}T12:00:00`);
    return `${d.toLocaleString(undefined, { month: 'long' })} ${cell.dayNum}`;
  }
  return String(cell.dayNum);
}

export function MonthPeekModal({ peek, onClose, onOpenDay }: MonthPeekModalProps) {
  if (!peek) return null;

  const { cell, rec } = peek;
  const tier = monthPerfTier(rec);
  const tc = MONTH_TIER[tier];
  const goalPct = rec ? Math.round((rec.production / (rec.goal || 22000)) * 100) : 0;
  const tint =
    tier === 'problem' ? 'var(--terracotta-tint)' : tier === 'soft' ? 'var(--gold-tint)' : 'var(--success-tint)';

  const stats = rec
    ? [
        { label: 'Production', value: `$${rec.production.toLocaleString()}`, tone: tc.fg },
        {
          label: 'Goal attainment',
          value: `${goalPct}%`,
          tone: goalPct >= 100 ? 'var(--success)' : 'var(--ink-0)',
        },
        { label: 'Patients seen', value: String(rec.visits), tone: 'var(--ink-0)' },
        { label: 'New patients', value: String(rec.newPatients || 0), tone: 'var(--ink-0)' },
        {
          label: 'No-shows',
          value: String(rec.noShows || 0),
          tone: (rec.noShows || 0) >= 5 ? 'var(--terracotta)' : 'var(--ink-0)',
        },
        { label: 'Cancellations', value: String(rec.cancellations || 0), tone: 'var(--ink-0)' },
      ]
    : [];

  return (
    <ViewportOverlay open onClose={onClose} scrimClassName="bg-[rgba(20,20,30,0.3)] backdrop-blur-[2px]">
      <div
        className={cn(
          'cal-modal w-[min(480px,calc(100vw-48px))]',
          'overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-0)] shadow-[var(--sh-3)]',
        )}
      >
        <div
          className="border-b border-[var(--cream-2)] px-[22px] py-[17px] pb-[15px]"
          style={{ background: `color-mix(in srgb, ${tint} 55%, var(--cream-1))` }}
        >
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h2 className="m-0 font-[var(--font-display)] text-[21px] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--ink-0)]">
                  {monthPeekTitle(cell)}
                </h2>
                {rec?.partial && (
                  <span className="rounded-[5px] bg-[var(--ai-tint)] px-[9px] py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-[var(--ai-3)]">
                    Today
                  </span>
                )}
                {rec?.projected && (
                  <span className="rounded-[5px] bg-[var(--cream-0)] px-[9px] py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-[var(--ink-3)]">
                    Projected
                  </span>
                )}
              </div>
              <div className="text-[12.5px] text-[var(--ink-2)]">
                {rec
                  ? (
                      <>
                        {goalPct >= 100 ? 'Above' : 'Below'} daily goal ·{' '}
                        <span className="font-[var(--font-mono)] font-semibold" style={{ color: tc.fg }}>
                          {goalPct}%
                        </span>{' '}
                        attainment
                      </>
                    )
                  : 'No production data recorded for this day yet.'}
              </div>
            </div>
            <IconButton icon="x" size={32} onClick={onClose} label="Close" />
          </div>
        </div>

        {rec ? (
          <div className="grid grid-cols-3 gap-x-4 gap-y-3.5 px-[22px] py-4 pb-1.5">
            {stats.map((s) => (
              <div key={s.label}>
                <div style={calendarEyebrow({ marginBottom: 3 })}>{s.label}</div>
                <div
                  className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em]"
                  style={{ color: s.tone }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-[22px] py-6 text-center text-[13px] text-[var(--ink-3)]">
            Day stats will appear here once production is captured.
          </div>
        )}

        {rec?.anomaly && (
          <div className="mx-[22px] mt-2 flex items-center gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_22%,transparent)] bg-[var(--terracotta-tint)] px-[13px] py-2.5 text-[12.5px] font-semibold text-[var(--terracotta)]">
            <Icon kind="alert" size={14} stroke={2.2} />
            {rec.anomaly}
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2 border-t border-[var(--cream-2)] bg-[var(--cream-1)] px-[22px] py-[13px]">
          <Button variant="secondary" size="sm">
            Day report
          </Button>
          <Button variant="primary" size="sm" iconLeft="arrow-r" onClick={onOpenDay}>
            Open in Day
          </Button>
        </div>
      </div>
    </ViewportOverlay>
  );
}
